
import React, { createContext, useEffect, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthContextType, AuthorProfile } from './types';
import {
  fetchUserProfile,
  fetchAuthorProfile,
  updateProfileToAuthor,
  createAuthorProfile,
  signInWithEmail,
  signUpWithEmail,
  signOutUser
} from './authService';
import { identifyUser, resetIdentity } from '@/lib/posthog';
import { setSentryUser, clearSentryUser, Sentry } from '@/lib/sentry';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [authorProfile, setAuthorProfile] = useState<AuthorProfile | null>(null);
  const [isAuthor, setIsAuthor] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState<'reader' | 'author'>('reader');

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          setTimeout(() => {
            handleProfileFetch(session.user.id, session.user.email);
          }, 0);
        } else {
          resetUserState();
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        handleProfileFetch(session.user.id, session.user.email);
      } else {
        setIsLoading(false);
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  
  const handleProfileFetch = async (userId: string, email?: string | null) => {
    try {
      const { data, error } = await fetchUserProfile(userId);
      
      if (data && !error) {
        setProfile(data);
        setIsAuthor(!!data.is_author);
        setIsAdmin(!!data.is_admin);

        const role = data.is_admin ? 'admin' : data.is_author ? 'author' : 'reader';
        identifyUser(userId, { email: email ?? undefined, role, username: data.username });
        setSentryUser({ id: userId, email: email ?? undefined, role });

        if (data.is_author) {
          handleAuthorProfileFetch(userId);
          const savedView = localStorage.getItem('preferredView') as 'reader' | 'author' || 'reader';
          setCurrentView(savedView);
        }
      } else {
        console.error("Error fetching profile:", error);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching user profile:", error);
      Sentry.captureException(error, { tags: { context: "handleProfileFetch" } });
      setIsLoading(false);
    }
  };

  const handleAuthorProfileFetch = async (userId: string) => {
    try {
      const { data, error } = await fetchAuthorProfile(userId);

      if (error) {
        console.error("Error fetching author profile:", error);
        return;
      }
      if (data) {
        setAuthorProfile({
          penName: data.pen_name,
          biography: data.biography,
          genres: data.genres,
          publishedWorks: data.published_works,
          authorSince: data.created_at
        });
      }
      // No row = author hasn't filled out their author profile yet — not an error
    } catch (error) {
      console.error("Error fetching author profile:", error);
      Sentry.captureException(error, { tags: { context: "handleAuthorProfileFetch" } });
    }
  };

  const resetUserState = () => {
    setProfile(null);
    setAuthorProfile(null);
    setIsAuthor(false);
    setIsAdmin(false);
    resetIdentity();
    clearSentryUser();
  };

  const signIn = async (email: string, password: string) => {
    const response = await signInWithEmail(email, password);
    return response;
  };

  const signUp = async (email: string, password: string, userData: any) => {
    const response = await signUpWithEmail(email, password, userData);
    return response;
  };

  const signOut = async () => {
    await signOutUser();
    resetUserState();
  };
  
  const applyForAuthorRole = async (authorData: AuthorProfile) => {
    if (!user) return { error: { message: 'User not authenticated' } };
    
    try {
      const { error: profileUpdateError } = await updateProfileToAuthor(user.id);
        
      if (profileUpdateError) throw profileUpdateError;
      
      const { error: authorProfileError } = await createAuthorProfile(user.id, authorData);
        
      if (authorProfileError) throw authorProfileError;
      
      setIsAuthor(true);
      setAuthorProfile(authorData);
      
      switchToAuthorView();
      
      return { error: null };
    } catch (error) {
      return { error };
    }
  };
  
  const switchToReaderView = () => {
    setCurrentView('reader');
    localStorage.setItem('preferredView', 'reader');
  };
  
  const switchToAuthorView = () => {
    if (isAuthor) {
      setCurrentView('author');
      localStorage.setItem('preferredView', 'author');
    }
  };

  const value = {
    session,
    user,
    profile,
    authorProfile,
    isAuthor,
    isAdmin,
    isLoading,
    signIn,
    signUp,
    signOut,
    applyForAuthorRole,
    switchToReaderView,
    switchToAuthorView,
    currentView
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
